import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader, ArrowRight, Gift } from 'lucide-react';
import { auth } from '../services/auth';

export default function RegisterForm({ onSuccess, onSwitchToLogin, trialOnly = false, initialCode = '' }) {
  const [invitation, setInvitation] = useState(trialOnly || Boolean(initialCode));
  const [form, setForm] = useState({ name: '', email: '', inviteCode: initialCode, password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const update = event => {
    setForm(previous => ({ ...previous, [event.target.name]: event.target.value }));
    setError('');
  };
  const submit = async event => {
    event.preventDefault();
    setError('');
    if (invitation && !form.inviteCode.trim()) { setError('Escribe tu código de invitación.'); return; }
    if (!invitation && form.password !== form.confirmPassword) { setError('Las contraseñas no coinciden.'); return; }
    setLoading(true);
    try {
      const result = await auth.register({ name: form.name.trim(), email: form.email.trim().toLowerCase(),
        ...(invitation ? { inviteCode: form.inviteCode.trim().toUpperCase() } : { password: form.password }) });
      onSuccess(result);
    } catch (err) { setError(err.message || 'No se pudo crear tu cuenta. Intenta de nuevo.'); }
    finally { setLoading(false); }
  };
  const inputClass = 'w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-morado focus:border-transparent';
  return (
    <div className="rounded-2xl border border-morado/10 bg-white p-6 sm:p-8 shadow-xl">
      <form onSubmit={submit} className="space-y-5" aria-busy={loading}>
        {!trialOnly && <label className="flex items-center gap-3 rounded-xl bg-morado/5 p-4 text-morado font-medium">
          <input type="checkbox" checked={invitation} onChange={e => { setInvitation(e.target.checked); setError(''); }} className="h-5 w-5 accent-morado" />
          Tengo un código para probar 5 días gratis
        </label>}
        <div>
          <label htmlFor="register-name" className="block text-sm font-medium text-gray-700 mb-2">Tu nombre</label>
          <input id="register-name" name="name" autoComplete="name" maxLength={120} required value={form.name} onChange={update} className={inputClass} placeholder="¿Cómo te llamas?" />
        </div>
        <div>
          <label htmlFor="register-email" className="block text-sm font-medium text-gray-700 mb-2">Correo electrónico</label>
          <input id="register-email" name="email" type="email" autoComplete="email" maxLength={254} required value={form.email} onChange={update} className={inputClass} placeholder="tu@correo.com" aria-describedby={invitation ? 'trial-email-help' : undefined} />
          {invitation && <p id="trial-email-help" className="mt-2 text-xs text-gray-500">Aquí recibirás tu usuario y contraseña temporal.</p>}
        </div>
        {invitation ? <div>
          <label htmlFor="register-code" className="block text-sm font-medium text-gray-700 mb-2">Código de invitación</label>
          <input id="register-code" name="inviteCode" autoCapitalize="characters" autoComplete="off" maxLength={32} required value={form.inviteCode} onChange={update} className={`${inputClass} uppercase tracking-widest`} placeholder="Escribe tu código" />
          <p className="mt-3 text-sm text-gray-600 flex gap-2"><Gift className="h-5 w-5 shrink-0 text-morado" aria-hidden="true" />Tus cinco días comienzan al activar tu cuenta. Sin tarjeta y sin cargos automáticos.</p>
        </div> : <>
          <div>
            <label htmlFor="register-password" className="block text-sm font-medium text-gray-700 mb-2">Contraseña</label>
            <input id="register-password" name="password" type="password" autoComplete="new-password" minLength={8} maxLength={128} required value={form.password} onChange={update} className={inputClass} placeholder="Mínimo 8 caracteres" />
          </div>
          <div>
            <label htmlFor="register-confirm" className="block text-sm font-medium text-gray-700 mb-2">Confirmar contraseña</label>
            <input id="register-confirm" name="confirmPassword" type="password" autoComplete="new-password" required value={form.confirmPassword} onChange={update} className={inputClass} />
          </div>
        </>}
        {error && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>}
        <button type="submit" disabled={loading} className="w-full rounded-xl bg-morado py-3 text-white font-semibold flex justify-center items-center gap-2 disabled:opacity-60">
          {loading ? <><Loader className="h-5 w-5 animate-spin" aria-hidden="true" />Creando tu cuenta…</> : <>{invitation ? 'Obtener mis 5 días gratis' : 'Crear mi cuenta'}<ArrowRight className="h-5 w-5" aria-hidden="true" /></>}
        </button>
        <p className="text-center text-sm text-gray-600">¿Ya tienes cuenta?{' '}{onSwitchToLogin
          ? <button type="button" onClick={onSwitchToLogin} className="text-morado font-semibold">Inicia sesión</button>
          : <Link to="/login" className="text-morado font-semibold">Inicia sesión</Link>}</p>
      </form>
    </div>
  );
}
